"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IncomeAttemptReasonCode = void 0;
/**
 * Normalized broker-facing *reason code* for why an income-verification attempt failed, shown in the
 * partner dashboard income card's attempts table. Mirrors the blessed pattern of `IncomeAttemptMethod`:
 * the backend classifies its churn-prone internal taxonomies (`SingleDocumentUploadRequestRejectionReason`
 * for uploaded documents, plus the known automatic-failure dispositions) INTO this small, stable domain
 * enum; the frontend maps each token to a human sentence fragment via Paraglide and composes the
 * displayed sentence (capitalize + join + punctuate). The backend NEVER returns the prose itself — that
 * keeps the broker contract from leaking internal UPPER_SNAKE tokens or hard-coding English on the wire.
 *
 * Values are *domain categories*, never display strings. Many internal tokens collapse here by design
 * (e.g. `OUTDATED_DOC`, `DISALLOWED_OUTDATED`, `LIEN_REFI_OUTDATED_DOC` all → `outdated`; the genuine
 * disallowed-type tokens → `documentTypeNotAllowed` while the engine-unsupported tokens →
 * `couldNotProcessDocument`). `unableToVerify` is the exhaustive-mapping safety
 * net for tokens with no broker-meaningful specific reason; a newly-added internal token is a compile
 * error in the classifier map until handled, so it can't silently vanish.
 *
 * There is no free-text escape hatch: an upstream description with no broker-meaningful code (e.g. an
 * IRS/Work Number/Experian internal developer note) surfaces NO reason at all (empty `reasonCodes`,
 * status-only row). The closed Plaid `incomeNotDerivedReasons` debug-token set is mapped to specific
 * codes on the backend; an unknown/legacy Plaid token collapses to the generic `unableToVerify`.
 */
var IncomeAttemptReasonCode;
(function (IncomeAttemptReasonCode) {
    // Document review verdicts (from VerificationDocumentPendingReason / DocumentDisallowedReason / LienRefiDocumentRejectionReason)
    IncomeAttemptReasonCode["unableToVerify"] = "unableToVerify";
    IncomeAttemptReasonCode["insufficientIncome"] = "insufficientIncome";
    IncomeAttemptReasonCode["incorrectDocumentType"] = "incorrectDocumentType";
    IncomeAttemptReasonCode["incomplete"] = "incomplete";
    IncomeAttemptReasonCode["duplicate"] = "duplicate";
    IncomeAttemptReasonCode["nonApplicant"] = "nonApplicant";
    IncomeAttemptReasonCode["continuationPage"] = "continuationPage";
    IncomeAttemptReasonCode["nonIncomeDocument"] = "nonIncomeDocument";
    IncomeAttemptReasonCode["cropped"] = "cropped";
    IncomeAttemptReasonCode["outdated"] = "outdated";
    IncomeAttemptReasonCode["missingInfo"] = "missingInfo";
    IncomeAttemptReasonCode["blurryOrCropped"] = "blurryOrCropped";
    IncomeAttemptReasonCode["couldNotVerifyAuthenticity"] = "couldNotVerifyAuthenticity";
    IncomeAttemptReasonCode["pictureOfScreen"] = "pictureOfScreen";
    IncomeAttemptReasonCode["nameMismatch"] = "nameMismatch";
    IncomeAttemptReasonCode["invalidDocument"] = "invalidDocument";
    IncomeAttemptReasonCode["missingScreenshots"] = "missingScreenshots";
    IncomeAttemptReasonCode["missingPages"] = "missingPages";
    IncomeAttemptReasonCode["documentTypeNotAllowed"] = "documentTypeNotAllowed";
    // The verification engine (Ocrolus/Google/Gemini) could not parse the document (e.g. unreadable
    // format, name mismatch). Distinct from `documentTypeNotAllowed`, which means the document TYPE
    // itself is disallowed — here the type may be fine but the engine couldn't extract from it.
    IncomeAttemptReasonCode["couldNotProcessDocument"] = "couldNotProcessDocument";
    IncomeAttemptReasonCode["insufficientNeedPriorYear"] = "insufficientNeedPriorYear";
    IncomeAttemptReasonCode["passwordProtected"] = "passwordProtected";
    IncomeAttemptReasonCode["missingData"] = "missingData";
    IncomeAttemptReasonCode["dataMismatch"] = "dataMismatch";
    IncomeAttemptReasonCode["incorrectMortgageStatement"] = "incorrectMortgageStatement";
    // Known automatic-attempt failure dispositions
    IncomeAttemptReasonCode["bankConnectionFailed"] = "bankConnectionFailed";
    // Mapped from the closed Plaid `incomeNotDerivedReasons` debug-token set (Python `ReportDebugInfo`):
    // INSUFFICIENT_DAYS_OF_INFLOWS → insufficientTransactionHistory; PLAID_INCOME_TOO_HIGH → incomeAboveThreshold.
    IncomeAttemptReasonCode["insufficientTransactionHistory"] = "insufficientTransactionHistory";
    IncomeAttemptReasonCode["incomeAboveThreshold"] = "incomeAboveThreshold";
    // On a VALIDATED app, verified income supports a SMALLER line than the one requested — out-of-bound per
    // the incomeComputationV2.py line-size bound (NOT below stated income). The green out-of-bound row's
    // reason: a verified outcome that explains the downsize.
    IncomeAttemptReasonCode["incomeSupportsSmallerLine"] = "incomeSupportsSmallerLine";
})(IncomeAttemptReasonCode || (exports.IncomeAttemptReasonCode = IncomeAttemptReasonCode = {}));
